import React, {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import styled from 'styled-components';

import IconButton from '../../../components/button/IconButton';
import ToolTip from '../../../components/tooltip/ToolTip';
import { MediaPreviewHandle } from '../../../types/media-preview';
import { formatTimecode, quantizeToFrame } from '../../../utils/timecode';
import { useTimelinePlayhead } from '../hooks/useTimelinePlayhead';
import type { WaveformPeaks } from '../hooks/useWaveformPeaks';
import { MaDrawerChipVisual } from '../ma/MaEditDrawer';
import { MaVariantGroup } from '../ma/maVariantGroups';
import {
  MaEditClipLayer,
  MaEditDropLanes,
  MaEditFooter,
  TimelineMaEditProps,
} from './TimelineMaEditLayer';
import TimelineRuler from './TimelineRuler';
import WaveformTrack, { TimelineClipView, resolveClipDuration } from './WaveformTrack';
import CommentPinLayer, { TimelinePin } from './CommentPinLayer';

export type { TimelinePin };
export type { TimelineClipView };
export type { TimelineMaEditProps };

/** マルチトラック表示（MA モード）の 1 トラック分 */
export interface TimelineTrackData {
  /** トラックヘッダーのラベル（"MOV" / "A1" / "A2"...） */
  label: string;
  /** オーディオトラック番号（1 始まり）。MOV トラックは undefined（編集対象外） */
  trackNo?: number;
  clips: TimelineClipView[];
}

/**
 * タイムラインチェックモードのパネル本体（Premiere Pro ライクな簡易タイムライン）。
 *
 * - プレイヘッド（縦ライン）が再生位置を示し、ルーラー / トラックのドラッグでスクラブ
 * - ⌘/Ctrl + ホイールでカーソル位置を中心にズーム、ホイールで水平スクロール
 * - コメントピンレーンに投稿者アバターを配置（ホバー / クリックで内容表示 + シーク）
 * - 下部に CommentField（親から ReactNode で受け取る）
 *
 * メディア切替時は親側で key={mediaItemId} を変えて再マウントする前提
 * （ズーム / スクロール状態のリセットを兼ねる）。
 */

export interface TimelinePanelProps {
  fps: number;
  /** API 由来のメディア長（秒）。再生ハンドル側の値が取れるまでのフォールバック */
  durationHint?: number | null;
  isPlaying: boolean;
  getMediaHandle: () => MediaPreviewHandle | null;
  peaks: WaveformPeaks | null;
  peaksLoading: boolean;
  peaksError: boolean;
  pins: TimelinePin[];
  /** ピンのポップオーバーからの解決済み / 進行中トグル */
  onPinResolveToggle?: (_pin: TimelinePin) => void;
  /** パネル下部に描画するコメント入力欄 */
  commentField: ReactNode;
  /** トラックヘッダーのラベル（既定 "A1"） */
  trackLabel?: string;
  /**
   * マルチトラック表示（MA モード）。指定時は peaks の単一トラックの代わりに
   * クリップ配置トラックを縦に並べる
   */
  tracks?: TimelineTrackData[];
  /** MA タイムライン編集モード（編集アイコン・クリップ編集・下ドロワー） */
  maEdit?: TimelineMaEditProps | null;
  /**
   * パネルを親の残り高さいっぱいまで広げ、トラックを縦に拡張する。
   * オーディオメディアでプレビュー領域を畳むタイムラインモード用
   */
  fillHeight?: boolean;
}

// ===== レイアウト定数 =====
const RULER_HEIGHT = 24;
const PIN_LANE_HEIGHT = 26;
const TRACK_HEIGHT = 56;
/** マルチトラック時の 1 トラック高さ（本数が多いときは圧縮） */
const MULTI_TRACK_HEIGHT = 44;
const MULTI_TRACK_HEIGHT_COMPACT = 32;
const HEADER_COL_WIDTH = 56;

/** ズーム上限: 1フレームあたり 12px */
const MAX_PX_PER_FRAME = 12;
/**
 * fit（全体表示・初回表示）時に右端へ残す余白。
 * 余白ゼロだと最後まで再生した瞬間にプレイヘッドが右端に密着し、
 * わずかなスクロールが発生して画面が動いて見えるため
 */
const FIT_RIGHT_MARGIN_PX = 16;
/** ズームボタン1回あたりの倍率 */
const ZOOM_STEP = 1.5;
/** 再生追従時に左端へ残す余白比率 */
const FOLLOW_MARGIN_RATIO = 0.1;

// ===== styled =====

const PanelContainer = styled.div<{ $fill?: boolean }>`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-top: 1px solid var(--soremo-border);
  user-select: none;

  ${p => p.$fill && `
    flex: 1 1 auto;
    min-height: 0;
  `}
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  height: 40px;
  padding: 0 12px;
  border-bottom: 1px solid var(--soremo-border);
`;

const TimecodeDisplay = styled.div`
  font-variant-numeric: tabular-nums;
  min-width: 88px;
`;

const Body = styled.div<{ $height: number; $fill?: boolean }>`
  display: flex;
  ${p => (p.$fill ? 'flex: 1 1 auto; min-height: 0; overflow: hidden;' : `height: ${p.$height}px;`)}
`;

const HeaderCol = styled.div`
  width: ${HEADER_COL_WIDTH}px;
  flex-shrink: 0;
  border-right: 1px solid var(--soremo-border);
  display: flex;
  flex-direction: column;
`;

const HeaderSpacer = styled.div<{ $height: number }>`
  height: ${p => p.$height}px;
  border-bottom: 1px solid var(--soremo-border);
`;

const TrackHeaderLabel = styled.div<{ $height: number }>`
  height: ${p => p.$height}px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--soremo-background, #f5f6f7);
`;

const ScrollArea = styled.div`
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.04);
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
`;

const TimelineContent = styled.div<{ $width: number; $height: number }>`
  position: relative;
  width: ${p => p.$width}px;
  min-width: 100%;
  height: ${p => p.$height}px;
  cursor: ew-resize;
`;

const PlayheadLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 1px;
  background: var(--soremo-blue);
  pointer-events: none;
  z-index: 10;
  will-change: transform;

  /* ルーラー部分のつまみ（逆三角形） */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -5px;
    border-left: 5.5px solid transparent;
    border-right: 5.5px solid transparent;
    border-top: 8px solid var(--soremo-blue);
  }
`;

const FieldRow = styled.div`
  border-top: 1px solid var(--soremo-border);
  padding: 4px 16px 8px;
  display: flex;
  justify-content: center;

  & > * {
    max-width: 720px;
  }
`;

/** クリップ選択時の詳細情報行（編集モード） */
const ClipInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
  padding: 4px 12px;
  border-top: 1px solid var(--soremo-border);
  background: var(--soremo-background, #f5f6f7);

  .clip-info-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .clip-info-item {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 4px;
    font-variant-numeric: tabular-nums;
  }
`;

// ===== 本体 =====

const TimelinePanel = ({
  fps,
  durationHint,
  isPlaying,
  getMediaHandle,
  peaks,
  peaksLoading,
  peaksError,
  pins,
  onPinResolveToggle,
  commentField,
  trackLabel = 'A1',
  tracks,
  maEdit = null,
  fillHeight = false,
}: TimelinePanelProps) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const playheadRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const [duration, setDuration] = useState<number>(durationHint ?? 0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [pixelsPerSecond, setPixelsPerSecond] = useState<number>(0);
  const [displayFrameIdx, setDisplayFrameIdx] = useState(0);
  /** 解決済みコメントのピン表示（既定は非表示） */
  const [showResolvedPins, setShowResolvedPins] = useState(false);

  /** fit（全体表示）状態か。true の間はリサイズ / duration 変化で自動追従 */
  const isFitRef = useRef(true);
  const isScrubbingRef = useRef(false);
  /** ズーム時にカーソル位置を維持するための scrollLeft 予約値 */
  const pendingScrollLeftRef = useRef<number | null>(null);

  const durationRef = useRef(duration);
  durationRef.current = duration;
  const ppsRef = useRef(pixelsPerSecond);
  ppsRef.current = pixelsPerSecond;
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;

  // fit 倍率は右端余白（FIT_RIGHT_MARGIN_PX）を差し引いた幅で算出する
  const fitWidth = Math.max(viewportWidth - FIT_RIGHT_MARGIN_PX, 0);
  const fitPps = duration > 0 && fitWidth > 0 ? fitWidth / duration : 0;
  // 極端に短いメディアでは fit 倍率がフレームズーム上限を超えるため、上限は両者の大きい方
  const maxPps = Math.max(fps * MAX_PX_PER_FRAME, fitPps);
  const minPps = fitPps > 0 ? Math.min(fitPps, maxPps) : 1;

  const clampPps = useCallback(
    (pps: number) => Math.max(minPps, Math.min(maxPps, pps)),
    [minPps, maxPps],
  );

  // ===== ビューポート幅の追跡 =====
  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width ?? 0;
      if (w > 0) setViewportWidth(w);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ===== fillHeight: 実際に確保された Body 高さを追跡（トラック縦拡張用） =====
  const [fillBodyHeight, setFillBodyHeight] = useState(0);
  useEffect(() => {
    if (!fillHeight) return;
    const el = bodyRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      const h = entries[0]?.contentRect.height ?? 0;
      if (h > 0) setFillBodyHeight(h);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [fillHeight]);

  // ===== fit 状態の自動追従（初期化・リサイズ・duration 確定時） =====
  useEffect(() => {
    if (isFitRef.current && fitPps > 0) {
      setPixelsPerSecond(fitPps);
    } else if (pixelsPerSecond === 0 && fitPps > 0) {
      setPixelsPerSecond(fitPps);
    }
  }, [fitPps, pixelsPerSecond]);

  // ===== ズーム後の scrollLeft 適用（カーソル位置維持） =====
  useLayoutEffect(() => {
    const el = scrollAreaRef.current;
    if (el && pendingScrollLeftRef.current !== null) {
      el.scrollLeft = Math.max(0, pendingScrollLeftRef.current);
      pendingScrollLeftRef.current = null;
      setScrollLeft(el.scrollLeft);
    }
  }, [pixelsPerSecond]);

  // ===== プレイヘッド更新（rAF・state を介さず transform を直接更新） =====
  const applyPlayheadPosition = useCallback((time: number) => {
    const line = playheadRef.current;
    if (line) {
      line.style.transform = `translateX(${time * ppsRef.current}px)`;
    }
  }, []);

  useTimelinePlayhead({
    enabled: true,
    getHandle: getMediaHandle,
    onFrame: (time, handleDuration) => {
      // duration はハンドル側の値を優先（HLS メタデータ読込後に確定する）
      if (handleDuration > 0 && Math.abs(handleDuration - durationRef.current) > 0.05) {
        setDuration(handleDuration);
      } else if (durationRef.current === 0 && durationHint && durationHint > 0) {
        setDuration(durationHint);
      }

      applyPlayheadPosition(time);

      // タイムコード表示はフレームが変わったときだけ更新（過剰な再レンダー防止）
      const frameIdx = Math.round(time * fps);
      setDisplayFrameIdx(prev => (prev === frameIdx ? prev : frameIdx));

      // 再生中はプレイヘッドを追従スクロール（ページ送り）。
      // 上限は「コンテンツ終端が右端に収まる位置」でクランプし、
      // 残りがすべて見えているときに終端より先の余白へスライドしないようにする
      // （プレイヘッドのつまみ等のはみ出しでスクロール可能幅が数 px 広がるため、
      //   ブラウザのクランプには頼らず duration 基準で計算する）
      const el = scrollAreaRef.current;
      if (el && isPlayingRef.current && !isScrubbingRef.current) {
        const x = time * ppsRef.current;
        const margin = el.clientWidth * FOLLOW_MARGIN_RATIO;
        if (x > el.scrollLeft + el.clientWidth - margin || x < el.scrollLeft) {
          const effectiveDuration = handleDuration > 0 ? handleDuration : durationRef.current;
          const maxScroll = Math.max(0, effectiveDuration * ppsRef.current - el.clientWidth);
          el.scrollLeft = Math.min(Math.max(0, x - margin), maxScroll);
        }
      }
    },
  });

  // pps 変更時にもプレイヘッド位置を即時反映
  useEffect(() => {
    const handle = getMediaHandle();
    if (handle) applyPlayheadPosition(handle.getCurrentTime());
  }, [pixelsPerSecond, getMediaHandle, applyPlayheadPosition]);

  // ===== シーク =====
  const seekToTime = useCallback((time: number) => {
    const handle = getMediaHandle();
    if (!handle) return;
    const max = durationRef.current > 0 ? durationRef.current : Infinity;
    const quantized = quantizeToFrame(Math.max(0, Math.min(max, time)), fps);
    handle.seekTo(quantized);
    applyPlayheadPosition(quantized);
    setDisplayFrameIdx(Math.round(quantized * fps));
  }, [getMediaHandle, fps, applyPlayheadPosition]);

  /** シーク先が画面外なら見える位置へスクロール（終端側は余白へ行き過ぎないようクランプ） */
  const ensureTimeVisible = useCallback((time: number) => {
    const el = scrollAreaRef.current;
    if (!el) return;
    const x = time * ppsRef.current;
    if (x < el.scrollLeft || x > el.scrollLeft + el.clientWidth) {
      const maxScroll = Math.max(0, durationRef.current * ppsRef.current - el.clientWidth);
      el.scrollLeft = Math.min(Math.max(0, x - el.clientWidth * FOLLOW_MARGIN_RATIO), maxScroll);
    }
  }, []);

  // ===== スクラブ（ルーラー / トラックのドラッグ） =====
  const timeFromClientX = useCallback((clientX: number): number => {
    const content = contentRef.current;
    if (!content || ppsRef.current <= 0) return 0;
    const rect = content.getBoundingClientRect();
    return (clientX - rect.left) / ppsRef.current;
  }, []);

  const isEditingMa = !!maEdit?.isEditing;
  const onSelectClipRef = useRef(maEdit?.onSelectClip);
  onSelectClipRef.current = maEdit?.onSelectClip;

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    // 編集モード中: 空き領域クリックで選択解除（クリップは stopPropagation 済み）
    onSelectClipRef.current?.(null);
    // 再生中のスクラブは一時停止（Premiere と同様）
    if (isPlayingRef.current) {
      getMediaHandle()?.pause();
    }
    isScrubbingRef.current = true;
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    seekToTime(timeFromClientX(e.clientX));
  }, [getMediaHandle, seekToTime, timeFromClientX]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isScrubbingRef.current) return;
    seekToTime(timeFromClientX(e.clientX));
  }, [seekToTime, timeFromClientX]);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isScrubbingRef.current) return;
    isScrubbingRef.current = false;
    (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
  }, []);

  // ===== スクロール / ズーム =====
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollLeft(e.currentTarget.scrollLeft);
  }, []);

  // ⌘/Ctrl + ホイール = ズーム、通常ホイール = 水平スクロール。
  // preventDefault が必要なため native リスナー（passive: false）で登録する。
  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const oldPps = ppsRef.current;
        if (oldPps <= 0) return;
        const factor = Math.exp(-e.deltaY * 0.002);
        const newPps = clampPps(oldPps * factor);
        if (newPps === oldPps) return;
        isFitRef.current = false;
        const cursorX = e.clientX - el.getBoundingClientRect().left;
        const timeAtCursor = (el.scrollLeft + cursorX) / oldPps;
        pendingScrollLeftRef.current = timeAtCursor * newPps - cursorX;
        setPixelsPerSecond(newPps);
      } else if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        // 縦ホイールを水平スクロールに変換
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [clampPps]);

  const zoomAroundPlayhead = useCallback((factor: number) => {
    const el = scrollAreaRef.current;
    const oldPps = ppsRef.current;
    if (!el || oldPps <= 0) return;
    const newPps = clampPps(oldPps * factor);
    if (newPps === oldPps) return;
    isFitRef.current = false;
    const handle = getMediaHandle();
    const time = handle ? handle.getCurrentTime() : (el.scrollLeft + el.clientWidth / 2) / oldPps;
    pendingScrollLeftRef.current = time * newPps - el.clientWidth / 2;
    setPixelsPerSecond(newPps);
  }, [clampPps, getMediaHandle]);

  const handleZoomIn = useCallback(() => zoomAroundPlayhead(ZOOM_STEP), [zoomAroundPlayhead]);
  const handleZoomOut = useCallback(() => zoomAroundPlayhead(1 / ZOOM_STEP), [zoomAroundPlayhead]);

  const handleFit = useCallback(() => {
    isFitRef.current = true;
    if (fitPps > 0) {
      pendingScrollLeftRef.current = 0;
      setPixelsPerSecond(fitPps);
    }
  }, [fitPps]);

  // ===== トランスポート =====
  const handleTogglePlay = useCallback(() => {
    getMediaHandle()?.clickToggle();
  }, [getMediaHandle]);

  const stepBy = useCallback((deltaSeconds: number) => {
    const handle = getMediaHandle();
    if (!handle) return;
    if (isPlayingRef.current) handle.pause();
    const next = handle.getCurrentTime() + deltaSeconds;
    seekToTime(next);
    ensureTimeVisible(next);
  }, [getMediaHandle, seekToTime, ensureTimeVisible]);

  const handleStepBackFrame = useCallback(() => stepBy(-1 / fps), [stepBy, fps]);
  const handleStepForwardFrame = useCallback(() => stepBy(1 / fps), [stepBy, fps]);

  // ===== ピンクリック =====
  const handlePinSelect = useCallback((pin: TimelinePin) => {
    const handle = getMediaHandle();
    if (handle && isPlayingRef.current) handle.pause();
    seekToTime(pin.timeSeconds);
    ensureTimeVisible(pin.timeSeconds);
  }, [getMediaHandle, seekToTime, ensureTimeVisible]);

  // ===== MA 編集モード: ドロワーチップの D&D =====
  const [activeDragGroup, setActiveDragGroup] = useState<MaVariantGroup | null>(null);
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDrawerDragStart = useCallback((event: DragStartEvent) => {
    const group = event.active.data.current?.group as MaVariantGroup | undefined;
    setActiveDragGroup(group ?? null);
  }, []);

  const handleDrawerDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDragGroup(null);
      if (!maEdit) return;
      const group = event.active.data.current?.group as MaVariantGroup | undefined;
      const overData = event.over?.data.current as { trackNo: number } | undefined;
      const translated = event.active.rect.current.translated;
      const content = contentRef.current;
      if (!group || !overData || !translated || !content || ppsRef.current <= 0) return;
      // ドラッグ中チップの左端位置 → タイムライン秒（開始位置）
      const rect = content.getBoundingClientRect();
      const seconds = (translated.left - rect.left) / ppsRef.current;
      maEdit.onAddClip(group, Math.max(0, seconds), overData.trackNo);
    },
    [maEdit],
  );

  // ===== 描画 =====
  const contentWidth = Math.max(viewportWidth, duration * pixelsPerSecond);
  // マルチトラック時は本数に応じて 1 トラックの高さを圧縮
  const isMultiTrack = !!tracks && tracks.length > 0;
  const baseTrackHeight = !isMultiTrack
    ? TRACK_HEIGHT
    : tracks.length >= 4
      ? MULTI_TRACK_HEIGHT_COMPACT
      : MULTI_TRACK_HEIGHT;
  // 編集モード中は最下段に「新規トラック」ドロップレーンを 1 本分追加
  const extraLaneCount = isEditingMa && isMultiTrack ? 1 : 0;
  const trackCount = (isMultiTrack ? tracks.length : 1) + extraLaneCount;
  // fillHeight: 確保された Body 高さに合わせてトラックを縦に拡張
  const trackHeight =
    fillHeight && fillBodyHeight > 0
      ? Math.max(
          baseTrackHeight,
          Math.floor((fillBodyHeight - RULER_HEIGHT - PIN_LANE_HEIGHT) / trackCount),
        )
      : baseTrackHeight;
  const bodyHeight = RULER_HEIGHT + PIN_LANE_HEIGHT + trackHeight * trackCount;
  const timecodeText = useMemo(
    () => formatTimecode(displayFrameIdx / fps, { fps, withFrames: true }),
    [displayFrameIdx, fps],
  );

  // 選択中クリップの詳細情報（編集モードのインフォ行用）
  const selectedClipInfo = useMemo(() => {
    const selectedId = maEdit?.selectedClipId;
    if (!selectedId || !tracks) return null;
    for (const track of tracks) {
      const clip = track.clips.find(c => c.editId === selectedId);
      if (clip) return { trackLabel: track.label, clip };
    }
    return null;
  }, [maEdit?.selectedClipId, tracks]);

  // 解決済みコメントのピンは既定で非表示（トグルで表示可能）
  const hasResolvedPins = useMemo(() => pins.some(p => p.isResolved), [pins]);
  const visiblePins = useMemo(
    () => (showResolvedPins ? pins : pins.filter(p => !p.isResolved)),
    [pins, showResolvedPins],
  );
  const handleToggleResolvedPins = useCallback(() => {
    setShowResolvedPins(prev => !prev);
  }, []);

  return (
    <PanelContainer $fill={fillHeight}>
      <DndContext
        sensors={dndSensors}
        onDragStart={handleDrawerDragStart}
        onDragEnd={handleDrawerDragEnd}
        onDragCancel={() => setActiveDragGroup(null)}
      >
      <Toolbar>
        {maEdit?.available && (
          <ToolTip
            description={isEditingMa ? '編集をやめる' : 'タイムラインを編集'}
            placement="top"
          >
            <IconButton
              icon="edit"
              size={20}
              filled={isEditingMa}
              color={isEditingMa ? 'var(--soremo-blue)' : undefined}
              onClick={isEditingMa ? maEdit.onCancel : maEdit.onEnterEdit}
            />
          </ToolTip>
        )}
        <div className="u-row u-items-center" style={{ gap: 4 }}>
          <ToolTip description="1フレーム戻る（←）" placement="top">
            <IconButton icon="chevron_left" size={22} onClick={handleStepBackFrame} />
          </ToolTip>
          <ToolTip description={isPlaying ? '停止（Space）' : '再生（Space）'} placement="top">
            <IconButton icon={isPlaying ? 'pause' : 'play_arrow'} size={26} filled onClick={handleTogglePlay} />
          </ToolTip>
          <ToolTip description="1フレーム進む（→）" placement="top">
            <IconButton icon="chevron_right" size={22} onClick={handleStepForwardFrame} />
          </ToolTip>
        </div>

        <TimecodeDisplay className="bodytext-13 u-text-black">{timecodeText}</TimecodeDisplay>
        <span className="bodytext-11 u-text-light-gray">{Math.round(fps * 100) / 100}fps</span>
        <span className="bodytext-11 u-text-light-gray">SHIFT+←→で1秒移動</span>

        <div className="u-row u-items-center" style={{ gap: 8, marginLeft: 'auto' }}>
          {hasResolvedPins && (
            <ToolTip
              description={showResolvedPins ? '解決済みコメントを隠す' : '解決済みコメントを表示'}
              placement="top"
            >
              <IconButton
                icon="check_circle"
                size={20}
                filled={showResolvedPins}
                color={showResolvedPins ? 'var(--soremo-blue)' : 'var(--soremo-light-gray)'}
                onClick={handleToggleResolvedPins}
              />
            </ToolTip>
          )}
          <ToolTip description="ズームアウト" placement="top">
            <IconButton icon="zoom_out" size={20} onClick={handleZoomOut} />
          </ToolTip>
          <ToolTip description="全体表示" placement="top">
            <IconButton icon="fit_screen" size={20} onClick={handleFit} />
          </ToolTip>
          <ToolTip description="ズームイン（⌘+スクロール）" placement="top">
            <IconButton icon="zoom_in" size={20} onClick={handleZoomIn} />
          </ToolTip>
        </div>
      </Toolbar>

      <Body ref={bodyRef} $height={bodyHeight} $fill={fillHeight}>
        <HeaderCol>
          <HeaderSpacer $height={RULER_HEIGHT} />
          <HeaderSpacer $height={PIN_LANE_HEIGHT} />
          {isMultiTrack ? (
            <>
              {tracks.map(track => (
                <TrackHeaderLabel
                  key={track.label}
                  $height={trackHeight}
                  className="label8 u-text-light-gray"
                >
                  {track.label}
                </TrackHeaderLabel>
              ))}
              {extraLaneCount > 0 && (
                <TrackHeaderLabel $height={trackHeight} className="label8 u-text-light-gray">
                  ＋
                </TrackHeaderLabel>
              )}
            </>
          ) : (
            <TrackHeaderLabel $height={trackHeight} className="label8 u-text-light-gray">
              {trackLabel}
            </TrackHeaderLabel>
          )}
        </HeaderCol>

        <ScrollArea ref={scrollAreaRef} onScroll={handleScroll}>
          <TimelineContent
            ref={contentRef}
            $width={contentWidth}
            $height={bodyHeight}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            <TimelineRuler
              duration={duration}
              fps={fps}
              pixelsPerSecond={pixelsPerSecond}
              scrollLeft={scrollLeft}
              viewportWidth={viewportWidth}
              height={RULER_HEIGHT}
            />
            <CommentPinLayer
              pins={visiblePins}
              pixelsPerSecond={pixelsPerSecond}
              height={PIN_LANE_HEIGHT}
              onPinSelect={handlePinSelect}
              onResolveToggle={onPinResolveToggle}
            />
            {isMultiTrack ? (
              tracks.map(track => (
                <WaveformTrack
                  key={track.label}
                  clips={track.clips}
                  duration={duration}
                  pixelsPerSecond={pixelsPerSecond}
                  scrollLeft={scrollLeft}
                  viewportWidth={viewportWidth}
                  height={trackHeight}
                />
              ))
            ) : (
              <WaveformTrack
                peaks={peaks}
                isLoading={peaksLoading}
                error={peaksError}
                duration={duration}
                pixelsPerSecond={pixelsPerSecond}
                scrollLeft={scrollLeft}
                viewportWidth={viewportWidth}
                height={trackHeight}
              />
            )}
            {/* MA 編集モード: クリップ選択 / トリムとドロップレーン */}
            {isEditingMa && maEdit && isMultiTrack && (
              <>
                <MaEditDropLanes
                  tracks={tracks}
                  topOffset={RULER_HEIGHT + PIN_LANE_HEIGHT}
                  trackHeight={trackHeight}
                />
                <MaEditClipLayer
                  tracks={tracks}
                  topOffset={RULER_HEIGHT + PIN_LANE_HEIGHT}
                  trackHeight={trackHeight}
                  pixelsPerSecond={pixelsPerSecond}
                  fps={fps}
                  selectedClipId={maEdit.selectedClipId}
                  contentRef={contentRef}
                  onSelectClip={maEdit.onSelectClip}
                  onTrimClip={maEdit.onTrimClip}
                />
              </>
            )}
            <PlayheadLine ref={playheadRef} />
          </TimelineContent>
        </ScrollArea>
      </Body>

      {/* クリップ選択時の詳細情報行（ファイル名・トラック・開始位置・尺） */}
      {isEditingMa && selectedClipInfo && (
        <ClipInfoRow>
          <span className="clip-info-name bodytext-11 u-text-black">
            {selectedClipInfo.clip.label || '(名称なし)'}
          </span>
          <span className="clip-info-item bodytext-11 u-text-light-gray">
            <span>トラック</span>
            <span className="u-text-black">{selectedClipInfo.trackLabel}</span>
          </span>
          <span className="clip-info-item bodytext-11 u-text-light-gray">
            <span>開始</span>
            <span className="u-text-black">
              {formatTimecode(selectedClipInfo.clip.startSeconds, { fps, withFrames: true })}
            </span>
          </span>
          <span className="clip-info-item bodytext-11 u-text-light-gray">
            <span>尺</span>
            <span className="u-text-black">
              {formatTimecode(resolveClipDuration(selectedClipInfo.clip), { fps, withFrames: true })}
            </span>
          </span>
          {selectedClipInfo.clip.optionSlotKey && (
            <span className="clip-info-item bodytext-11 u-text-light-gray">
              オプション候補由来のため、ここでは編集できません（音の差し替えは候補チップで）
            </span>
          )}
        </ClipInfoRow>
      )}

      {isEditingMa && maEdit ? (
        <MaEditFooter maEdit={maEdit} />
      ) : (
        <FieldRow>{commentField}</FieldRow>
      )}

      {/* ドラッグ中のチップ表示（ポインタ追従） */}
      <DragOverlay dropAnimation={null}>
        {activeDragGroup && maEdit && (
          <MaDrawerChipVisual
            group={activeDragGroup}
            overrides={maEdit.variantOverrides}
            dragging={false}
          />
        )}
      </DragOverlay>
      </DndContext>
    </PanelContainer>
  );
};

export default TimelinePanel;
